import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { AuthHttpService } from './auth-http.service';
import { Router } from '@angular/router';
import { AuthModel } from '../models/auth.model';
import { RegistrationModel } from '../models/registration.model';
import { UserModel } from '../models/user.model';
import { AppConstants } from '../../app-constants';
import {UserRoleModel} from "../models/user-role.model";
import {UserInfoModel} from "../models/user-info.model";

export type UserType = UserModel | undefined;

type Role = 'editor' | 'admin';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private unsubscribe: Subscription[] = [];
  private authLocalStorageToken = AppConstants.APP_AUTH;

  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;

  private rolePermissions: { [key in Role]: string[] } = {
    editor: [],
    admin: ['tabCoCau','dichVu','goiCuoc']
  };

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHttpService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  register(payload: RegistrationModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.signup(payload).pipe(
      map((res) => {
        this.isLoadingSubject.next(false);
        return res;
      }),
      catchError((err) => {
        return of(null);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  login(username: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(username, password).pipe(
      map((res: any) => {
        return this.setAuthFromLocalStorage(res);
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        return throwError(err);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    this.currentUserSubject.next(undefined);
    localStorage.removeItem(this.authLocalStorageToken);
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.accessToken).pipe(
      map((res: any) => {
        const user = new UserModel();
        user.setUser(res.body);

        if (user) {
          this.currentUserSubject.next(user);
          this.setRole(user.roles);
          console.log(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  updateUser(payload:any) {
    return this.authHttpService.updateUser(payload).pipe(
      map((res) => {
        // @ts-ignore
        this.currentUserSubject.next(res);
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  getUserDetail(id: string): Observable<UserModel> {
    return this.authHttpService.getUserDetail(id).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  lockUser(id: string) {
    return this.authHttpService.lockUser(id).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  unLockUser(id: string) {
    return this.authHttpService.unLockUser(id).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  deleteUser(id: any) {
    this.isLoadingSubject.next(true);
    return this.authHttpService.deleteUser(id).pipe(
      map((res) => {
        this.isLoadingSubject.next(false);
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<string> {
    return this.authHttpService.changePassword(oldPassword, newPassword).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  confirmForgotPassword(token: string, newPassword: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .confirmForgotPassword(token, newPassword)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getAllUsers(): Observable<UserInfoModel[]> {
    return this.authHttpService.getAllUsers().pipe(
      map((res: UserInfoModel[]) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  findAllUser(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.findAll(params).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err) => {
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  getAllRoles(): Observable<UserRoleModel[]> {
    return this.authHttpService.getAllRoles();
  }

  private setAuthFromLocalStorage(res: any): boolean {
    const auth: AuthModel = res.body.token;
    if (auth && auth.accessToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  setTokenAuth(token: any) {
    let auth = this.getAuthFromLocalStorage();
    if (auth && auth.accessToken) {
      auth.accessToken = token;
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
    }
  }

  getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }
      return JSON.parse(lsValue);
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  private currentUserRole: Role = 'editor';

  hasPermission(permission: string): boolean {
    const permissions = this.rolePermissions[this.currentUserRole];
    return permissions.includes(permission);
  }

  setRole(role: string | null) {
    if (role === 'ROLE_ADMIN') {
      this.currentUserRole = 'admin';
    } else {
      this.currentUserRole = 'editor';
    }
  }

  getRole(): Role {
    return this.currentUserRole;
  }

  getToken() {
    let auth = this.getAuthFromLocalStorage();
  }

  refreshToken(token: string) {
    return this.authHttpService.refreshToken(token);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
